import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useField, useFormikContext } from 'formik'
import { TagCategoryEnum, addCustomTagRequest, selectSortedSubmissionTags, selectTagCategory } from 'state/constants'
import { Tag } from 'services/api/constants'
import { PartiposterTagType, selectPartiposterTags } from 'state/partiposter'

import Dropdown from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'

interface SubmissionTagsSelectorProp {
    fieldName: string
}

interface TagOption {
    label: string
    value: string
    isDisabled: boolean
}

export const SubmissionTagsSelector: React.FC<SubmissionTagsSelectorProp> = ({ fieldName }) => {
    const [t] = useTranslation()
    const [field, meta] = useField(fieldName)
    const [searchTag, setSearchTag] = useState<string | null>(null)
    const { values, setFieldValue } = useFormikContext<any>()
    const dispatch = useDispatch()
    const sortedSubmissionTags = useSelector(selectSortedSubmissionTags)
    const tagCategory = useSelector(selectTagCategory(TagCategoryEnum.SUBMISSION_TAG))
    const partiposterTags = useSelector(selectPartiposterTags(PartiposterTagType.ADD))
    const isUpdateLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.AddCustomTag, STATE_KEYS.GetTags]))

    const selectableTags = useMemo<{ standard: TagOption[]; custom: TagOption[] }>(() => {
        const { standard, custom } = sortedSubmissionTags

        const isTagOptionDisabled = (tagUuid: string) => {
            return (
                values.profileTags.some(
                    (selectedTag: { uuid: string; text: string }) => selectedTag.uuid === tagUuid,
                ) || partiposterTags.some(tag => tag.tag_uuid === tagUuid)
            )
        }

        return {
            standard: standard.map(
                t =>
                    ({
                        ...t,
                        isDisabled: isTagOptionDisabled(t.uuid),
                    }) as TagOption,
            ),
            custom: custom.map(
                t =>
                    ({
                        ...t,
                        isDisabled: isTagOptionDisabled(t.uuid),
                    }) as TagOption,
            ),
        }
    }, [values, partiposterTags, sortedSubmissionTags])

    const tagsOptions = useMemo(() => {
        const returnData: { standard: TagOption[] | null; custom: TagOption[] | null; hasSearchedTag: boolean } = {
            standard: null,
            custom: null,
            hasSearchedTag: false,
        }

        if (searchTag !== null) {
            const searchLowerCase = searchTag.toLowerCase()
            const filteredStandard = selectableTags.standard.filter(item => {
                if (item.label.toLowerCase() === searchLowerCase) {
                    returnData.hasSearchedTag = true
                }
                return item.label.toLowerCase().includes(searchLowerCase)
            })

            const filteredCustom = selectableTags.custom.filter(item => {
                if (item.label.toLowerCase() === searchLowerCase) {
                    returnData.hasSearchedTag = true
                }
                return item.label.toLowerCase().includes(searchLowerCase)
            })

            returnData.standard = filteredStandard.length > 0 ? filteredStandard : null
            returnData.custom = filteredCustom.length > 0 ? filteredCustom : null
        } else {
            returnData.standard = selectableTags.standard.length > 0 ? selectableTags.standard : null
            returnData.custom = selectableTags.custom.length > 0 ? selectableTags.custom : null
        }
        return returnData
    }, [selectableTags, searchTag])

    const onSearchTag = (value: string) => {
        setSearchTag(value)
    }
    const onClearSearchTag = () => {
        setSearchTag(null)
    }

    return (
        <Dropdown
            value={field.value}
            label={t('submission.table.tagsDateLabel')}
            isRequired
            errorMessage={meta.error}
            maxWidth={'100%'}
            isDisabled={isUpdateLoading}
            placeholder={t('placeholderText.selectOrCreateNewTags')}
            size="s"
        >
            <MenuItem.Search
                onSearch={onSearchTag}
                onClearSearch={onClearSearchTag}
                placeholder={t('placeholderText.selectOrCreateNewTags')}
            />
            <MenuItem.Title text={t('submission.tagHistory.standardTags')} size="m" />
            {tagsOptions.standard ? (
                tagsOptions.standard.map((t: TagOption, index: number) => {
                    return (
                        <MenuItem.Text
                            tabIndex={index}
                            key={t.value}
                            text={t.label}
                            isDisabled={t.isDisabled}
                            value={t.value}
                            onClick={() => {
                                setSearchTag(null)
                                setFieldValue(fieldName, t.value)
                            }}
                        />
                    )
                })
            ) : (
                <EmptySearchResult />
            )}

            <MenuItem.Divider />
            <MenuItem.Title text={t('submission.tagHistory.customizedTags')} size="m" />

            {tagsOptions.custom ? (
                tagsOptions.custom.map((t: TagOption, index: number) => {
                    const prefixIndex = tagsOptions.custom?.length ? tagsOptions.custom?.length : 1000
                    const tabIndex = index + prefixIndex
                    return (
                        <MenuItem.Text
                            tabIndex={tabIndex}
                            key={t.value}
                            text={t.label}
                            isDisabled={t.isDisabled}
                            value={t.value}
                            onClick={() => {
                                setSearchTag(null)
                                setFieldValue(fieldName, t.value)
                            }}
                        />
                    )
                })
            ) : (
                <EmptySearchResult />
            )}

            {searchTag && !tagsOptions.hasSearchedTag && (
                <>
                    <MenuItem.Divider />
                    <MenuItem.Title text={t('submission.tagHistory.action')} size="m" />
                </>
            )}

            {searchTag && !tagsOptions.hasSearchedTag && (
                <MenuItem.Text
                    text={`${t('button.createTags')} ${searchTag}`}
                    onClick={() => {
                        setSearchTag(null)
                        if (tagCategory && tagCategory.uuid) {
                            dispatch(
                                addCustomTagRequest({
                                    name: searchTag,
                                    categoryUuid: tagCategory.uuid,
                                    categoryEnum: TagCategoryEnum.SUBMISSION_TAG,
                                    onSuccessCallback: (tag?: Tag) => {
                                        if (tag?.uuid) {
                                            setFieldValue(fieldName, tag?.uuid)
                                        }
                                    },
                                }),
                            )
                        }
                    }}
                />
            )}
        </Dropdown>
    )
}

export default SubmissionTagsSelector
